import React, {type MouseEventHandler, useCallback, useEffect, useRef, useState} from 'react';
import type {EnrichedAstNode} from 'rendition-core';
import {useKeyBind} from 'Hooks/useKeyBind';
import {useNodeNameEditStore} from 'stores/nodeEdit';
import {useHighlighterContext} from 'Contexts/HighlighterContext';
import useDoubleClick from 'Hooks/useDoubleClick';
import classNames from 'classnames';

interface Props extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement> {
    node: EnrichedAstNode;
    updateView: (regroup?: boolean) => void;
    jumpToEditNextNode: () => void;
    highlightOnEdit?: boolean;
    onClick?: MouseEventHandler;
}

export const EditableNodeName: React.FC<React.PropsWithChildren<Props>> = ({
    node,
    updateView,
    jumpToEditNextNode,
    highlightOnEdit = true,
    onClick,
    ...props
}) => {
    const {clearHighlightByType, addNodeHighlight} = useHighlighterContext();
    const stopEditingAndSaveNodeId = useNodeNameEditStore.use.stopEditingAndSaveNodeId();
    const startEditing = useNodeNameEditStore.use.startEditing();
    const editingNodeNameId = useNodeNameEditStore.use.editingNodeNameId();
    const editingName = useNodeNameEditStore.use.editingName();

    const showEdit = editingNodeNameId === node.internalId && editingName;

    const ref = useRef<HTMLInputElement>(null);

    const [name, setName] = useState(node.uniqueName);

    const submitChangeName = useCallback(() => {
        node.uniqueName = name;
        updateView(false);
        stopEditingAndSaveNodeId();
    }, [name, node, stopEditingAndSaveNodeId, updateView]);

    const closeEdit = useCallback(() => {
        setName(node.uniqueName);
        stopEditingAndSaveNodeId();
    }, [node.uniqueName, stopEditingAndSaveNodeId]);

    useEffect(() => {
        if (highlightOnEdit && editingNodeNameId && editingName) {
            const id = editingNodeNameId;
            addNodeHighlight(id, 'Hover');
            return () => {
                clearHighlightByType('Hover');
            };
        }
    }, [addNodeHighlight, clearHighlightByType, editingNodeNameId, editingName, highlightOnEdit]);

    const nameRef = useRef<HTMLHeadingElement | null>(null);

    useDoubleClick({
        onSingleClick: onClick,
        onDoubleClick: (e) => startEditing(node.internalId),
        ref: nameRef,
        latency: 300,
    });

    // We need to conditional logic here to make sure that the keybind recreates when the ref mounts,
    // if not the ref will just be null and Mousetrap won't recognize the input as a source for keybinds
    useKeyBind('escape', showEdit ? closeEdit : () => {}, ref);
    useKeyBind('enter', submitChangeName, ref);

    return showEdit ? (
        <input
            autoFocus
            onBlur={closeEdit}
            onFocus={(e) => e.target.select()}
            className={classNames(
                'w-full flex-shrink flex-grow-0 basis-full rounded-lg border border-zinc-700 bg-sky-500/70 px-2 py-0.5',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-transparent focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300'
            )}
            value={name}
            onChange={(e) => setName(e.target.value)}
            ref={ref}
        />
    ) : (
        <h1
            {...props}
            ref={nameRef}
            className="w-full select-none overflow-hidden overflow-ellipsis whitespace-nowrap text-[12px] font-bold"
        >
            {node.uniqueName}
        </h1>
    );
};
